/**
 * `SHELL-041`/`SHELL-081`. Pausing loses nothing -- unlike restart or quit,
 * there is no destructive-action copy to write, so this is not a
 * `ConfirmDialog`: one control, not two, and `Escape` resumes rather than
 * answering a question.
 *
 * The play area itself (HUD and board both) goes `inert` while this is open
 * -- `Play.tsx`'s job, not this component's, since `inert` has to reach the
 * host div this component knows nothing about. An opaque full-viewport
 * overlay already blocks pointer input by sitting on top of everything;
 * `inert` is what also pulls keyboard focus and Tab order out of the board
 * underneath, which a `z-index` alone does not.
 */

import { useLayoutEffect, useRef } from 'preact/hooks';

import { focusScreenHeading } from '@shell/screen-transition';

export interface PauseOverlayProps {
  onResume: () => void;
}

export function PauseOverlay({ onResume }: PauseOverlayProps) {
  const resumeRef = useRef<HTMLButtonElement | null>(null);

  useLayoutEffect(() => {
    resumeRef.current?.focus();
    return () => {
      focusScreenHeading();
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onResume();
    }
    // Tab is left alone, deliberately: there is exactly one focusable
    // element here, so a wrap-to-self is what the browser's own default Tab
    // handling already does -- nothing to intercept.
  };

  return (
    <div class="dialog-overlay">
      <div
        class="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pause-heading"
        onKeyDown={onKeyDown}
      >
        <h2 id="pause-heading">Paused</h2>
        <div class="dialog__actions">
          <button ref={resumeRef} type="button" onClick={onResume}>
            Resume
          </button>
        </div>
      </div>
    </div>
  );
}
